import { useMemo } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { FilterableTable } from "../../lib/funnel/FilterableTable";
import type { ColumnSpec } from "../../lib/funnel/FilterableTable";
import { LEADS_SQL } from "./sql";

interface LeadRow {
  external_id: string;
  name: string;
  email: string;
  firm: string;
  advice_type: string;
  licensee: string;
  conference: string;
  note: string;
  team_member: string;
  country_code: string;
  created_date_utc: string;
}

function mapRows(columns: string[], rows: unknown[][]): LeadRow[] {
  return rows.map((r) => {
    const obj: Record<string, unknown> = {};
    columns.forEach((c, i) => {
      obj[c] = r[i];
    });
    return obj as unknown as LeadRow;
  });
}

function fmtTimestamp(val: unknown): string {
  if (!val) return "";
  const d = new Date(String(val));
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const COLUMNS: ColumnSpec<LeadRow>[] = [
  { key: "name", label: "Name", filterable: true },
  { key: "email", label: "Email" },
  { key: "firm", label: "Firm", filterable: true },
  { key: "advice_type", label: "Advice Type", filterable: true },
  { key: "licensee", label: "Licensee", filterable: true },
  { key: "conference", label: "Conference", filterable: true },
  { key: "team_member", label: "Team Member", filterable: true },
  { key: "country_code", label: "Country", filterable: true },
  {
    key: "created_date_utc",
    label: "Created",
    render: (_row, val) => fmtTimestamp(val),
  },
  { key: "note", label: "Note" },
];

export function Leads() {
  const result = useAdminQuery(LEADS_SQL, [], { refetchInterval: 30_000 });

  const rows = useMemo<LeadRow[]>(() => {
    if (!result.data) return [];
    return mapRows(result.data.columns, result.data.rows as unknown[][]);
  }, [result.data]);

  return (
    <>
      <Topbar crumbs={["Dashboards", "Leads"]} />
      <div className="page">
        <div className="page-header">
          <h1>Leads</h1>
          <p className="lede">
            Live feed from lead capture forms. Auto-refreshes every 30s.
          </p>
        </div>

        {result.isLoading && <p>Loading...</p>}
        {result.isError && <p>Error loading leads.</p>}

        {!result.isLoading && !result.isError && (
          <div className="card" style={{ padding: "1rem" }}>
            <FilterableTable<LeadRow>
              rows={rows}
              columns={COLUMNS}
              rowKey={(row) => row.external_id}
              searchAccessor={(row) =>
                `${row.name} ${row.email} ${row.firm} ${row.note ?? ""}`
              }
              searchPlaceholder="Search leads..."
              initialSortKey="created_date_utc"
              initialSortDir="desc"
              pageSize={50}
              toolbar={
                <span style={{ fontSize: "0.85rem", opacity: 0.7 }}>
                  {rows.length} lead{rows.length !== 1 ? "s" : ""}
                </span>
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
